import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FileWidget, Tabs} from '@core/components';
import {Attachment, EmployeeAccessInvitation, EmployeeAccessStatus, EmployeeCourse, EmployeeDetail, EmployeePolicy, EmployeeRow} from '@core/types';
import {AttachmentService, EmployeeService} from '@core/services';

@Component({
  selector: 'core-modal-employee-file',
  imports: [
    Tabs,
    FileWidget
  ],
  standalone: true,
  templateUrl: './employee-file.html',
  styleUrl: './employee-file.scss',
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;padding: 0 1.5rem 1.5rem'
  }
})
export class EmployeeFile implements OnInit, OnDestroy {
  readonly tabSkeletonItems = [1, 2, 3];
  private readonly accessFeedbackTtlMs = 4000;

  @Output()
  edit = new EventEmitter();
  @Output()
  accessUpdated = new EventEmitter<{employeeId: string | number; accessStatus: EmployeeAccessStatus}>();
  @Input() employee: EmployeeRow | null = null;

  courses: EmployeeCourse[] = [];
  coursesError = '';
  isLoadingCourses = false;
  policies: EmployeePolicy[] = [];
  policiesError = '';
  isLoadingPolicies = false;
  detail: EmployeeDetail | null = null;
  detailError = '';
  isLoadingDetail = false;
  isLoadingAvatar = false;
  attachmentsError = '';
  accessInvitations: EmployeeAccessInvitation[] = [];
  accessError = '';
  accessFeedback = '';
  isLoadingAccess = false;
  isInvitingAccess = false;
  avatarBlobUrl = '';
  private accessFeedbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private employeeService = inject(EmployeeService);
  private attachmentService = inject(AttachmentService);

  ngOnInit() {
    if (this.employee?.id) {
      this.loadDetail(this.employee.id);
      this.loadCourses(this.employee.id);
      this.loadPolicies(this.employee.id);
      this.loadAccess(this.employee.id);
    }
  }

  ngOnDestroy() {
    this.clearAccessFeedbackTimeout();
    this.revokeAvatarBlobUrl();
  }

  private async loadCourses(employeeId: string | number) {
    this.isLoadingCourses = true;
    this.coursesError = '';
    const response = await this.employeeService.getEmployeeCourses(employeeId, {skipLoader: true});
    this.isLoadingCourses = false;

    if (!response.success || !response.result) {
      this.coursesError = response.message || 'No se pudieron cargar los cursos.';
      return;
    }

    this.courses = response.result;
  }

  private async loadPolicies(employeeId: string | number) {
    this.isLoadingPolicies = true;
    this.policiesError = '';
    const response = await this.employeeService.getEmployeePolicies(employeeId, {skipLoader: true});
    this.isLoadingPolicies = false;

    if (!response.success || !response.result) {
      this.policiesError = response.message || 'No se pudieron cargar las políticas.';
      return;
    }

    this.policies = response.result;
  }

  private async loadDetail(employeeId: string | number) {
    this.isLoadingDetail = true;
    this.detailError = '';
    this.attachmentsError = '';
    const response = await this.employeeService.getEmployeeDetail(employeeId, {skipLoader: true});
    this.isLoadingDetail = false;

    if (!response.success || !response.result) {
      this.detailError = response.message || 'No se pudo cargar la ficha del empleado.';
      return;
    }

    this.detail = response.result;
    await this.resolveAvatar();
  }

  private async loadAccess(employeeId: string | number) {
    this.isLoadingAccess = true;
    this.accessError = '';
    const response = await this.employeeService.getEmployeeAccessInvitations({employeeId}, {skipLoader: true});
    this.isLoadingAccess = false;

    if (!response.success || !response.result) {
      this.accessError = response.message || 'No se pudo cargar el estado de acceso.';
      return;
    }

    this.accessInvitations = response.result;
  }

  avatarSrc() {
    return this.avatarBlobUrl || '';
  }

  handleAvatarError(event: Event) {
    const target = event.target as HTMLImageElement | null;
    if (target) {
      target.removeAttribute('src');
    }
  }

  get attachments() {
    return this.detail?.attachments || [];
  }

  get contractFileName() {
    return this.detail?.metadata?.contractFileName || this.employee?.metadata?.contractFileName || '';
  }

  get accessStatus(): EmployeeAccessStatus {
    return this.employeeService.resolveEmployeeAccessStatus(this.detail || this.employee, this.accessInvitations);
  }

  get currentCompanyAccessRole() {
    return this.detail?.userRole || this.employee?.userRole || null;
  }

  get accessRoleLabel(): string {
    if (this.currentCompanyAccessRole?.name) {
      return this.currentCompanyAccessRole.name;
    }

    const position = this.detail?.metadata?.position || this.employee?.metadata?.position || '';
    return String(position || '').trim();
  }

  get canInviteAccess(): boolean {
    return !this.isInvitingAccess && this.accessStatus !== 'active' && this.accessStatus !== 'pending' && !!this.employee?.id;
  }

  get pendingAccessInvitation(): EmployeeAccessInvitation | null {
    return this.accessInvitations.find(item => item.status === 'pending' && !!item.token) || null;
  }

  get accessInvitationManagementUrl(): string {
    const token = this.pendingAccessInvitation?.token;
    if (!token || typeof window === 'undefined') return '';
    return `${window.location.origin}/employee-access-invitations/${token}`;
  }

  resolveAccessLabel(status: EmployeeAccessStatus): string {
    return ({
      active: 'Acceso habilitado',
      pending: 'Invitación enviada',
      rejected: 'Invitación rechazada',
      expired: 'Invitación vencida',
      no_access: 'Sin acceso a plataforma'
    } as Record<EmployeeAccessStatus, string>)[status];
  }

  isContractAttachment(fileName?: string) {
    return !!fileName && !!this.contractFileName && fileName === this.contractFileName;
  }

  async invitePlatformAccess() {
    if (!this.employee?.id || this.isInvitingAccess) return;

    this.isInvitingAccess = true;
    this.accessError = '';
    this.accessFeedback = '';

    const response = await this.employeeService.createEmployeeAccessInvitation(this.employee.id, {skipLoader: true});
    this.isInvitingAccess = false;

    if (!response.success || !response.result) {
      this.accessError = response.message || 'No se pudo generar la invitación de acceso.';
      this.scheduleAccessFeedbackClear('error');
      return;
    }

    this.mergeInvitation(response.result);
    this.syncEmployeeAccessStatus();
    this.accessFeedback = 'Invitación de acceso generada correctamente.';
    this.scheduleAccessFeedbackClear('success');
  }

  async downloadAttachment(attachment: Attachment) {
    if (!attachment?.id) return;

    const response = await this.attachmentService.downloadAttachment(attachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.attachmentsError = response.message || 'No se pudo descargar el adjunto.';
      return;
    }

    this.attachmentsError = '';
    const url = URL.createObjectURL(response.result);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'adjunto';
    link.click();
    URL.revokeObjectURL(url);
  }

  async copyInvitationLink() {
    const url = this.accessInvitationManagementUrl;
    if (!url || typeof navigator === 'undefined' || !navigator.clipboard) {
      this.accessError = 'No se pudo copiar el enlace desde este navegador.';
      this.scheduleAccessFeedbackClear('error');
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      this.accessFeedback = 'Enlace de invitación copiado.';
      this.accessError = '';
      this.scheduleAccessFeedbackClear('success');
    } catch {
      this.accessError = 'No se pudo copiar el enlace.';
      this.scheduleAccessFeedbackClear('error');
    }
  }

  private async resolveAvatar() {
    this.revokeAvatarBlobUrl();
    this.isLoadingAvatar = true;
    const avatarAttachment = this.attachments.find(attachment => (attachment.mimeType || '').startsWith('image/'));
    if (!avatarAttachment?.id) {
      this.isLoadingAvatar = false;
      return;
    }

    const response = await this.attachmentService.downloadAttachment(avatarAttachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.isLoadingAvatar = false;
      return;
    }

    this.avatarBlobUrl = URL.createObjectURL(response.result);
    this.isLoadingAvatar = false;
  }

  private revokeAvatarBlobUrl() {
    if (!this.avatarBlobUrl) return;
    URL.revokeObjectURL(this.avatarBlobUrl);
    this.avatarBlobUrl = '';
  }

  private mergeInvitation(invitation: EmployeeAccessInvitation) {
    this.accessInvitations = [
      invitation,
      ...this.accessInvitations.filter(item => String(item.id) !== String(invitation.id))
    ];
  }

  private syncEmployeeAccessStatus() {
    const nextStatus = this.accessStatus;
    if (this.employee) {
      this.employee.accessStatus = nextStatus;
    }
    if (this.detail) {
      this.detail.accessStatus = nextStatus;
    }
    if (this.employee?.id !== undefined && this.employee?.id !== null) {
      this.accessUpdated.emit({
        employeeId: this.employee.id,
        accessStatus: nextStatus
      });
    }
  }

  private scheduleAccessFeedbackClear(type: 'success' | 'error') {
    this.clearAccessFeedbackTimeout();
    this.accessFeedbackTimeoutId = setTimeout(() => {
      if (type === 'success') {
        this.accessFeedback = '';
        return;
      }

      this.accessError = '';
    }, this.accessFeedbackTtlMs);
  }

  private clearAccessFeedbackTimeout() {
    if (!this.accessFeedbackTimeoutId) return;
    clearTimeout(this.accessFeedbackTimeoutId);
    this.accessFeedbackTimeoutId = null;
  }
}
